import { useEffect, useRef, useState } from 'react';

export const useScrollSpy = <T>(onActiveChange?: (data: T | null) => void) => {
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const elements = useRef(new Map<HTMLElement, { key: string; data: T }>());

  const register = (key: string, data: T) => (el: HTMLElement | null) => {
    if (el) elements.current.set(el, { key, data });
  };

  useEffect(() => {
    let frame = 0;
    let current: string | null = null;

    const measure = () => {
      frame = 0;
      const centre = window.innerHeight / 2;

      let nearest: { key: string; data: T } | null = null;
      let nearestDistance = Infinity;
      let listTop = Infinity;
      let listBottom = -Infinity;

      for (const [element, entry] of elements.current) {
        const { top, bottom } = element.getBoundingClientRect();
        if (top === 0 && bottom === 0) continue;

        listTop = Math.min(listTop, top);
        listBottom = Math.max(listBottom, bottom);

        const distance = Math.abs((top + bottom) / 2 - centre);

        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearest = entry;
        }
      }

      const active = centre >= listTop && centre <= listBottom ? nearest : null;
      const key = active?.key ?? null;

      if (key !== current) {
        current = key;
        setActiveKey(key);
        onActiveChange?.(active?.data ?? null);
      }
    };

    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };

    measure();
    document.fonts?.ready.then(measure).catch(() => {});
    document.addEventListener('scroll', schedule, {
      passive: true,
      capture: true,
    });
    window.addEventListener('resize', schedule);

    return () => {
      document.removeEventListener('scroll', schedule, { capture: true });
      window.removeEventListener('resize', schedule);
      cancelAnimationFrame(frame);
      setActiveKey(null);
      onActiveChange?.(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { activeKey, register };
};
